/*
 * Copyright 2024 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Token } from "./Token.js";

export type Events = {
  signIn: CustomEvent<Token>;
  signOut: Event;
  refresh: CustomEvent<Token>;
};

export interface BaseOauthClient<K extends keyof Events & string> {
  (): Promise<string>;

  getTokenOrUndefined: () => string | undefined;
  signIn: () => Promise<Token>;
  signOut: () => Promise<void>;

  addEventListener: <T extends K>(
    type: T,
    listener: ((evt: Events[T]) => void) | null,
    options?: boolean | AddEventListenerOptions,
  ) => void;

  removeEventListener: <T extends K>(
    type: T,
    callback: ((evt: Events[T]) => void) | null,
    options?: EventListenerOptions | boolean,
  ) => void;
}
